import { useCallback, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';

import { sanitizeFilename } from '../../components/utils/sanitizeFilename';
import { Constants } from '../../Constants';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { ChatExportResult } from '../models/ChatExport';
import { BackendServiceUrl } from '../services/BaseService';
import { ChatService } from '../services/ChatService';
import { downloadJson } from '../utils/downloadJson';

// Outcome surfaced to the user after an export attempt. `error` = nothing downloaded; `warning` =
// the file downloaded but some chats were missing; `info` = blocked by the 14-day cooldown. Rendered
// by the caller (the /history route has no global <Alerts/> container, so the result is shown inline).
export interface ExportAlert {
    intent: 'error' | 'warning' | 'info';
    message: string;
}

// The 14-day window ends at an exact instant, so the rate-limit message shows date AND time — a
// date-only string let a user retry at 00:00 on the eligible date and hit another 409. Mirror
// ChatHistoryList's locale choice (i18n 'en' → the UK format reads better; other languages pass
// through). Falls back to the raw value if the timestamp doesn't parse.
const formatEligibleAt = (iso: string, language: string): string => {
    const locale = language === 'en' ? 'en-GB' : language;
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(locale);
};

/**
 * Exports the current user's selected conversations to a single PII-filtered JSON file. Delegates
 * everything to the backend (POST /chats/export): the 14-day cooldown, ownership checks, and PII
 * redaction all run server-side. Returns the outcome so the caller (HistoryPage) can close the
 * confirm dialog and clear the selection only on success.
 */
export const useChatExport = () => {
    const { t, i18n } = useTranslation();
    const auth = useAuth();
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const [isExporting, setIsExporting] = useState(false);
    const [exportAlert, setExportAlert] = useState<ExportAlert | undefined>(undefined);

    const dismissExportAlert = useCallback(() => {
        setExportAlert(undefined);
    }, []);

    const exportChats = useCallback(
        async (chatIds: string[]): Promise<ChatExportResult> => {
            if (isExporting || chatIds.length === 0) {
                return { status: 'error' };
            }

            // Guard A: mirror the server cap client-side so a doomed request is never fired and the
            // user is told exactly why. Select-all already truncates to the cap, so this only trips on
            // a manual over-selection.
            if (chatIds.length > Constants.MAX_CHATS_PER_EXPORT) {
                setExportAlert({
                    intent: 'warning',
                    message: t('chatHistory.exportTooMany', { limit: Constants.MAX_CHATS_PER_EXPORT }),
                });
                return { status: 'too_many' };
            }

            const token = auth.user?.access_token ?? '';
            const chatService = new ChatService(token, BackendServiceUrl);
            setIsExporting(true);
            setExportAlert(undefined);
            try {
                const result = await chatService.exportChatHistoryAsync(chatIds, hostContext);

                switch (result.status) {
                    case 'success': {
                        // Every selected chat was skipped (all deleted/missing between listing and
                        // export) => the file has no chats. Don't download an empty file; show a
                        // distinct "nothing exported" notice instead of the partial warning, which reads
                        // "some" and misleads at zero. Backend guarantees >=1 chat in the file whenever
                        // any survived (else it takes the empty-owned branch), so all-skipped is the
                        // only empty case. De-dupe to mirror the backend's Distinct() on the ids.
                        const requestedCount = new Set(chatIds).size;
                        if (result.payload.skippedChatCount >= requestedCount) {
                            setExportAlert({ intent: 'warning', message: t('chatHistory.exportEmpty') });
                            break;
                        }

                        const nowIso = new Date().toISOString();
                        const filename = sanitizeFilename(
                            `${t('chatHistory.exportFilename')}-${nowIso.slice(0, 10)}.json`,
                        );
                        downloadJson(filename, result.payload.export);
                        if (result.payload.skippedChatCount > 0) {
                            // The file downloaded but is missing some chats (deleted/unreadable). Warn
                            // so a partial export isn't mistaken for the full history.
                            setExportAlert({ intent: 'warning', message: t('chatHistory.exportPartial') });
                        }
                        break;
                    }
                    case 'rate_limited':
                        setExportAlert({
                            intent: 'info',
                            message: result.nextEligibleAt
                                ? t('chatHistory.exportRateLimited', {
                                      date: formatEligibleAt(result.nextEligibleAt, i18n.language),
                                  })
                                : t('chatHistory.exportRateLimitedNoDate'),
                        });
                        break;
                    case 'too_many':
                        // Server rejected the selection as over-cap (defense-in-depth if the client
                        // guard drifts from the server). Show the server's cap when it sent one.
                        setExportAlert({
                            intent: 'warning',
                            message: t('chatHistory.exportTooMany', {
                                limit: result.maxChats ?? Constants.MAX_CHATS_PER_EXPORT,
                            }),
                        });
                        break;
                    case 'disabled':
                    case 'error':
                    default:
                        setExportAlert({ intent: 'error', message: t('chatHistory.exportError') });
                        break;
                }

                return result;
            } catch (e: unknown) {
                console.error('Failed to export chats:', e);
                setExportAlert({ intent: 'error', message: t('chatHistory.exportError') });
                return { status: 'error' };
            } finally {
                setIsExporting(false);
            }
        },
        [auth.user?.access_token, hostContext, i18n.language, isExporting, t],
    );

    return { exportChats, isExporting, exportAlert, dismissExportAlert };
};
